import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CVSSSeverity, Finding, FindingStatus } from '../../types/finding';
import { FromApplicationContext } from '../../types/application';
import { FINDING_STATUS_LABEL } from '../../design-system/severity';
import { SEVERITY_META, SeverityKey } from './severityDisplay';
import FindingRowActions from './FindingRowActions';
import { getFindingTitle, isDerivedFindingTitle } from '../../utils/findingUtils';
import { getFindingFixTime } from '../../utils/fixTime';

interface FindingsTableProps {
  findings: Finding[];
  fromApplication: FromApplicationContext;
  canWrite: boolean;
  /** Id of the finding whose action is currently in flight. */
  busyFindingId?: string | null;
  onProposeFix: (finding: Finding) => void;
  onViewFix: (finding: Finding) => void;
  onUpdateStatus: (finding: Finding, status: FindingStatus) => void;
}

/** Solid status chips — the triage state is the one thing every row must read at a glance. */
const STATUS_CHIP_BG: Record<FindingStatus, string> = {
  open: 'var(--lime)',
  needs_manual_review: 'var(--status-review)',
  resolved: 'var(--status-resolved)',
  ignored: 'var(--status-muted)',
  false_positive: 'var(--status-muted)',
};

/** CVSS scores are tinted with the severity band they fall into, so both pills agree. */
const cvssSeverityKey = (score: number, severity?: CVSSSeverity): SeverityKey => {
  const named = severity?.toLowerCase();
  if (named === 'critical' || named === 'high' || named === 'medium' || named === 'low') return named;
  if (score >= 9) return 'critical';
  if (score >= 7) return 'high';
  if (score >= 4) return 'medium';
  return 'low';
};

const HEADER_CELL_CLASS =
  'text-left font-mono text-[length:var(--fs-eyebrow)] font-medium uppercase tracking-[var(--tracking-eyebrow)] text-[color:var(--text-muted)] py-2.5 px-3 sm:px-4 whitespace-nowrap';

const CELL_CLASS = 'py-3 px-3 sm:px-4 align-middle';

/** Severity / CVSS pills share one geometry so the row reads as a single band. */
const PILL_CLASS =
  'inline-flex items-center rounded-[var(--radius-xs)] border px-2 py-[3px] font-mono text-[length:var(--fs-2xs)] font-medium';

/**
 * Dense findings table: severity, finding, file, CVSS and estimated fix time, with the
 * triage status and a per-row actions menu on the right.
 *
 * Layout is fluid: `table-fixed` with the text columns sized in `%` and the pill columns
 * in px, so titles and file paths take the slack on wide screens and truncate instead of
 * pushing the right-hand columns out of view. Secondary columns drop out at narrow widths
 * rather than forcing a horizontal scroll.
 */
const FindingsTable: React.FC<FindingsTableProps> = ({
  findings,
  fromApplication,
  canWrite,
  busyFindingId,
  onProposeFix,
  onViewFix,
  onUpdateStatus,
}) => {
  const location = useLocation();

  if (findings.length === 0) return null;

  return (
    <div className="rounded-[var(--radius-lg)] border border-[color:var(--border-subtle)] bg-[var(--surface-1)] overflow-hidden">
      <table className="w-full table-fixed">
        <caption className="sr-only">Security findings</caption>
        <thead>
          <tr className="bg-[var(--surface-2)]">
            <th scope="col" className={`${HEADER_CELL_CLASS} w-[92px] sm:w-[108px]`}>
              Severity
            </th>
            <th scope="col" className={HEADER_CELL_CLASS}>
              Finding
            </th>
            <th scope="col" className={`${HEADER_CELL_CLASS} hidden md:table-cell w-[24%]`}>
              File
            </th>
            <th scope="col" className={`${HEADER_CELL_CLASS} hidden lg:table-cell w-[76px]`}>
              CVSS
            </th>
            <th
              scope="col"
              className={`${HEADER_CELL_CLASS} hidden lg:table-cell w-[96px]`}
              title="Rough estimate of the effort to remediate, from the finding's severity and type"
            >
              Fix time
            </th>
            <th scope="col" className={`${HEADER_CELL_CLASS} hidden sm:table-cell w-[100px]`}>
              <span className="sr-only">Status</span>
            </th>
            <th scope="col" className={`${HEADER_CELL_CLASS} w-[48px] sm:w-[56px]`}>
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {findings.map((finding) => {
            const severityMeta = SEVERITY_META[finding.severity as SeverityKey] ?? SEVERITY_META.medium;
            const title = getFindingTitle(finding);
            const score = finding.cvss_base_score;
            const cvssMeta =
              score !== undefined && score !== null
                ? SEVERITY_META[cvssSeverityKey(score, finding.cvss_severity)]
                : null;

            return (
              <tr
                key={finding.id}
                className="relative border-t border-[color:var(--border-subtle)] hover:bg-[var(--surface-2)] transition-colors"
              >
                <td className={CELL_CLASS}>
                  <span className={`${PILL_CLASS} ${severityMeta.bg} ${severityMeta.text} ${severityMeta.border}`}>
                    {severityMeta.label}
                  </span>
                </td>

                <td className={CELL_CLASS}>
                  <Link
                    to={`/repositories/${fromApplication.appId}/findings/${finding.id}${location.search}`}
                    title={title}
                    className={`block truncate text-[length:var(--fs-md)] font-semibold text-[color:var(--text-primary)] after:absolute after:inset-0 after:content-['']${
                      isDerivedFindingTitle(finding) ? ' capitalize' : ''
                    }`}
                  >
                    {title}
                  </Link>
                </td>

                <td className={`${CELL_CLASS} hidden md:table-cell`}>
                  <span
                    className="block truncate font-mono text-[length:var(--fs-xs)] text-[color:var(--text-muted)]"
                    title={finding.file_path}
                  >
                    {finding.file_path}
                  </span>
                </td>

                <td className={`${CELL_CLASS} hidden lg:table-cell`}>
                  {cvssMeta && score !== undefined && score !== null ? (
                    <span
                      className={`${PILL_CLASS} ${cvssMeta.bg} ${cvssMeta.text} ${cvssMeta.border}`}
                      title={finding.cvss_vector ? `CVSS Vector: ${finding.cvss_vector}` : undefined}
                    >
                      {score.toFixed(1)}
                    </span>
                  ) : (
                    <span
                      className={`${PILL_CLASS} bg-[var(--surface-3)] text-[color:var(--text-muted)] border-[color:var(--border-subtle)]`}
                      title="No CVSS score for this finding"
                    >
                      N/A
                    </span>
                  )}
                </td>

                <td
                  className={`${CELL_CLASS} hidden lg:table-cell whitespace-nowrap text-[length:var(--fs-sm)] text-[color:var(--text-secondary)]`}
                >
                  {getFindingFixTime(finding)}
                </td>

                <td className={`${CELL_CLASS} hidden sm:table-cell`}>
                  <span
                    className="inline-flex items-center rounded-[var(--radius-xs)] px-2.5 py-[3px] text-[length:var(--fs-2xs)] font-semibold"
                    style={{
                      background: STATUS_CHIP_BG[finding.status] ?? 'var(--status-muted)',
                      color: 'var(--lime-fg)',
                    }}
                  >
                    {FINDING_STATUS_LABEL[finding.status] ?? finding.status}
                  </span>
                </td>

                <td className={CELL_CLASS}>
                  <FindingRowActions
                    finding={finding}
                    canWrite={canWrite}
                    isBusy={busyFindingId === finding.id}
                    onProposeFix={onProposeFix}
                    onViewFix={onViewFix}
                    onUpdateStatus={onUpdateStatus}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default FindingsTable;
